import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Acesso, TELAS_DISPONIVEIS } from '../types/perfil/Perfil';
import { perfilService } from '../services/PerfilService';
import { UsuarioPerfil } from '../types/enums/UsuarioPerfil';

interface UseAcessoResult {
  temAcesso: (tela: string, tipo?: 'visualizacao' | 'alteracao') => boolean;
  loading: boolean;
  acessos: Acesso[];
}

export const useAcesso = (): UseAcessoResult => {
  const { user } = useAuth();
  const [acessos, setAcessos] = useState<Acesso[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const carregarAcessos = async () => {
      if (!user?.guid) {
        console.log('useAcesso: Usuário não encontrado, definindo loading como false');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('useAcesso: Carregando acessos para usuário:', user.email, 'Role:', user.role);
        
        // Se for admin ou owner, não precisa carregar acessos - acesso total liberado
        if (user.role === UsuarioPerfil.THINKWAY_ADMIN || user.role === UsuarioPerfil.OWNER || user.role === UsuarioPerfil.EMPRESA_ADMIN) {
          console.log('useAcesso: Usuário admin/owner detectado - liberando acesso total');
          setAcessos([]); // Array vazio pois a lógica está na função temAcesso
          return;
        }

        // Para outros usuários, buscar o perfil e seus acessos
        try {
          console.log('useAcesso: Buscando perfil do usuário...');
          const perfilUsuario = await perfilService.buscarPerfilDoUsuarioAtual();
          
          if (perfilUsuario?.acessos && perfilUsuario.acessos.length > 0) {
            // É colaborador com perfil associado, usar os acessos do perfil
            setAcessos(perfilUsuario.acessos);
            console.log('useAcesso: Colaborador - Acessos carregados do perfil associado:', perfilUsuario.acessos);
          } else {
            // Não é colaborador ou colaborador sem perfil - usuário normal tem acesso total
            const acessosCompletos: Acesso[] = TELAS_DISPONIVEIS.map(tela => ({
              tela: tela.codigo,
              visualizacao: true,
              alteracao: true
            }));
            setAcessos(acessosCompletos);
            console.log('useAcesso: Usuário normal - acesso total liberado');
          }
        } catch (error: any) {
          console.error('useAcesso: Erro ao buscar perfil do usuário:', error);
          console.error('useAcesso: Status do erro:', error.response?.status);
          
          if (error.response?.status === 404) {
            // 404 significa que o usuário não tem perfil específico - usuário normal
            const acessosCompletos: Acesso[] = TELAS_DISPONIVEIS.map(tela => ({
              tela: tela.codigo,
              visualizacao: true,
              alteracao: true
            }));
            setAcessos(acessosCompletos);
            console.log('useAcesso: Usuário não tem perfil (404) - liberando acesso total');
          } else if (error.response?.status === 401) {
            console.error('useAcesso: Erro 401 - problema de autenticação!');
            setAcessos([]);
          } else {
            // Em caso de outros erros, assumir usuário normal com acesso total
            const acessosCompletos: Acesso[] = TELAS_DISPONIVEIS.map(tela => ({
              tela: tela.codigo,
              visualizacao: true,
              alteracao: true
            }));
            setAcessos(acessosCompletos);
            console.log('useAcesso: Erro desconhecido - assumindo usuário normal');
          }
        }

      } catch (error) {
        console.error('useAcesso: Erro geral ao carregar acessos:', error);
        setAcessos([]);
      } finally {
        setLoading(false);
      }
    };

    carregarAcessos();
  }, [user]);

  const temAcesso = (tela: string, tipo: 'visualizacao' | 'alteracao' = 'visualizacao'): boolean => {
    console.log(`useAcesso.temAcesso: Verificando acesso para tela="${tela}", tipo="${tipo}"`);
    
    // Se não há usuário logado, não tem acesso
    if (!user?.guid) {
      console.log('useAcesso.temAcesso: Usuário não logado - negando acesso');
      return false;
    }

    // Se for ThinkWay Admin ou OWNER, sempre tem acesso total - sem verificações adicionais
    if (user.role.toLocaleLowerCase() === UsuarioPerfil.THINKWAY_ADMIN.toLocaleLowerCase() || user.role.toLocaleLowerCase() === UsuarioPerfil.OWNER.toLocaleLowerCase()) {
      console.log(`useAcesso.temAcesso: Usuário ${user.role} - liberando acesso total`);
      return true;
    }

    // Se for Empresa Admin, tem acesso a tudo exceto perfis de acesso e configurações do sistema
    if (user.role.toLocaleLowerCase() === UsuarioPerfil.EMPRESA_ADMIN.toLocaleLowerCase()) {
      const hasAccess = tela !== 'perfis' && tela !== 'configuracoes-usuario';
      console.log(`useAcesso.temAcesso: Empresa Admin - tela=${tela}, acesso=${hasAccess}`);
      return hasAccess;
    }

    // Para usuários normais, verificar nos acessos específicos do perfil
    console.log('useAcesso.temAcesso: Verificando acessos específicos. Acessos disponíveis:', acessos);
    const acesso = acessos.find(a => a.tela === tela);
    
    if (!acesso) {
      console.log(`useAcesso.temAcesso: Acesso não encontrado para tela "${tela}" - negando`);
      return false;
    }

    const result = tipo === 'visualizacao' ? acesso.visualizacao : acesso.alteracao;
    console.log(`useAcesso.temAcesso: Acesso encontrado - ${tipo}=${result}`);
    return result;
  };

  return {
    temAcesso,
    loading,
    acessos
  };
};